/** @format */

import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
	ProductCatalogService,
	ProductListCompareService,
	ProductListSnackbarService
} from '@plp/data-access/services';
import {
	addCompareProduct,
	removeCompareProduct,
	setCompareBarProducts,
	setCompareProductsAvailability,
	setIsCompareGridOpen
} from '../compare/compare.actions';
import { catchError, Observable, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { COMPARE_PRODUCT_LIST_LIMIT } from '@plp/data-access/constants';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { selectCompareBarProducts } from './compare.selectors';
import {
	changeHeaderServiceStoreInfo,
	selectUserStoreInfo,
	setUserStoreInfoFromCookie
} from '@pdp/data-access/state';
import { CompareBarProduct, HeaderStoreSelection } from '@shared/data-access/interfaces';
import { isPlatformServer } from '@angular/common';
import { ProductAvailabilityCollection } from '@plp/data-access/interfaces';

@Injectable()
export class CompareEffects {
	setCompareBarProducts$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(addCompareProduct),
				withLatestFrom(this.store.select(selectCompareBarProducts)),
				tap(([, compareBarProducts]) => {
					if (compareBarProducts.length > COMPARE_PRODUCT_LIST_LIMIT) {
						// Error path
						this.plpSnackbarService.openSnackbar(
							'Only 5 products can be added to compare. Please remove a product before making another selection.'
						);
						const compareBarProductsCopy = [...compareBarProducts];
						compareBarProductsCopy.pop();
						this.store.dispatch(
							setCompareBarProducts({ compareBarProducts: compareBarProductsCopy })
						);
						return;
					}
					this.compareService.updateSessionStorage(compareBarProducts);
				})
			),
		{ dispatch: false }
	);

	removeCompareProduct$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(removeCompareProduct),
				withLatestFrom(this.store.select(selectCompareBarProducts)),
				tap(([action, compareBarProducts]) => {
					const productsAfterRemoval = compareBarProducts.filter(
						(cp) => cp.ecode !== action.ecode
					);
					this.store.dispatch(
						setCompareBarProducts({ compareBarProducts: productsAfterRemoval })
					);
					this.compareService.updateSessionStorage(productsAfterRemoval);
				})
			),
		{ dispatch: false }
	);

	/**
	 * @description
	 * When product list is updated, get the availability information and set data in state
	 */
	updateCompareProductAvailability$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					setIsCompareGridOpen,
					setUserStoreInfoFromCookie,
					changeHeaderServiceStoreInfo
				),
				withLatestFrom(
					this.store.select(selectCompareBarProducts),
					this.store.select(selectUserStoreInfo)
				),
				switchMap(
					([, compareBarProducts, userSelectedStore]: [
						Action,
						CompareBarProduct[],
						HeaderStoreSelection
					]) =>
						this.productAvailability(
							compareBarProducts.map((product) => product.ecode),
							userSelectedStore
						)
				),
				tap((compareProductsAvailability: ProductAvailabilityCollection) => {
					if (compareProductsAvailability?.ecodeMessagingMap) {
						this.store.dispatch(
							setCompareProductsAvailability({ compareProductsAvailability })
						);
					}
				})
			),
		{ dispatch: false }
	);

	constructor(
		private readonly plpSnackbarService: ProductListSnackbarService,
		private readonly compareService: ProductListCompareService,
		private readonly productCatalogService: ProductCatalogService,
		private readonly actions$: Actions,
		private readonly store: Store,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {}

	private productAvailability(
		ecodes: string[],
		store: HeaderStoreSelection
	): Observable<ProductAvailabilityCollection | null | any[]> {
		if (isPlatformServer(this.platformId) || !ecodes?.length) {
			return of(null);
		}

		return this.productCatalogService
			.getAvailabilityData(
				ecodes,
				true,
				store?.zipcode || store?.zip,
				store?.store || store?.location
			)
			.pipe(catchError(() => of([])));
	}
}
